import { useCallback, useContext, useEffect, useState } from 'react';
import useAlert from '../Alerts/useAlert';
import { Lot, LotStatus, defaultLot } from '../Types/Lot';
import { ErrorResponse } from '../Types/ErrorResponse';
import { correlationId, useHttpUtils } from './useHttpUtils';
import LotCountContext from '../Contexts/LotCountContext';
import { Quality } from '../Types/LotDetailQuality';
import { Genealogy } from '../Types/Genealogy';
import { useTranslation } from 'react-i18next';
import luxonReviver from './LuxonDateTimeReviver';

function useLotDetails(lotNumber: string, initialLotDetails?: Lot) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const { t } = useTranslation();

    const { updateCounters } = useContext(LotCountContext);

    const [lotDetails, setLotDetails] = useState<Lot>(initialLotDetails ?? defaultLot);

    const [qualities, setQualities] = useState<Quality[]>([]);
    const [genealogy, setGenealogy] = useState<Genealogy[]>([]);

    const [errorCode, setErrorCode] = useState<number>();
    const [loadingLotDetails, setLoadingLotDetails] = useState<boolean>(true);
    const [loadingQualities, setLoadingQualities] = useState<boolean>(false);
    const [loadingGenealogy, setLoadingGenealogy] = useState<boolean>(false);

    const getLotDetails = useCallback((revisionId?: number, fetchQuietly: boolean = false) => {
        if (!fetchQuietly) {
            setLoadingLotDetails(true);
        }

        let url = `api/v1/lot/${lotNumber}/detail`;
        if (revisionId) {
            url += `?revisionId=${revisionId}`;
        }

        return fetchDataAuthenticated(url)
            .then(response => {
                if (response.ok) {
                    return response.text();
                }

                setErrorCode(response.status);
                throw new Error(`Failed to get lot detail for ${lotNumber} [${correlationId}]`);
            })
            .then(text => {
                return text && JSON.parse(text, luxonReviver);
            })
            .then((data: Lot) => {
                setLotDetails(l => ({ ...l, ...data }));
                return data;
            })
            .catch(() => {
                const errorResponse: ErrorResponse = { statusCode: errorCode ?? 500 };

                if (errorCode === 404) {
                    errorResponse.message = "Lot not found";
                } else if (errorCode === 403) {
                    errorResponse.message = "Access denied";
                } else {
                    errorResponse.message = "An error occurred while fetching lot details";
                }

                return errorResponse;
            })
            .finally(() => {
                setLoadingLotDetails(false);
            });
    }, [fetchDataAuthenticated, lotNumber, setAlert]);

    const getLotQualities = useCallback((revisionId?: number, fetchQuietly: boolean = false): Promise<void> => {
        if (!lotNumber) {
            return Promise.resolve();
        }

        if (!fetchQuietly) {
            setLoadingQualities(true);
        }

        let url = `api/v1/lot/${lotNumber}/qualities`;
        if (revisionId) {
            url += `?revisionId=${revisionId}`;
        }

        return fetchDataAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.json();
                } else if (response.status === 404) {
                    throw new Error(`Lot ${lotNumber} not found`);
                }
                throw new Error(`Failed to get lot qualities for ${lotNumber} [${correlationId}]`);
            })
            .then((data: Quality[]) => {
                setQualities(data);
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoadingQualities(false);
            });
    }, [fetchDataAuthenticated, lotNumber, setAlert]);

    const getLotGenealogy = useCallback((revisionId?: number, fetchQuietly: boolean = false): Promise<void> => {
        if (!lotNumber) {
            return Promise.resolve();
        }

        if (!fetchQuietly) {
            setLoadingGenealogy(true);
        }

        let url = `api/v1/lot/${lotNumber}/genealogy`;
        if (revisionId) {
            url += `?revisionId=${revisionId}`;
        }

        return fetchDataAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.json();
                } else if (response.status === 404) {
                    throw new Error(`Lot ${lotNumber} not found`);
                } else if (response.status === 403) {
                    throw new Error(`User does not have authorization to view lot details: ${lotNumber} [${correlationId}]`);
                }
                throw new Error(`Failed to get lot detail for ${lotNumber} [${correlationId}]`);
            })
            .then((data: Genealogy[]) => {
                setGenealogy(data);
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoadingGenealogy(false);
            });
    }, [fetchDataAuthenticated, lotNumber, setAlert]);

    useEffect(() => {
        if (lotNumber && (lotDetails === defaultLot || lotDetails?.summary?.lotNumber !== lotNumber)) {
            getLotDetails();
        } else {
            if (lotDetails?.summary?.lotNumber) {
                setLoadingLotDetails(false)
            }
        }
    }, [getLotDetails, lotDetails, lotNumber]);

    useEffect(() => {
        if (!loadingLotDetails && qualities) {
            setLotDetails(l => l && ({ ...l, qualities: qualities }));
        }

        if (!loadingLotDetails && genealogy) {
            setLotDetails(l => l && ({ ...l, genealogy: genealogy }));
        }
    }, [loadingLotDetails, qualities, genealogy]);

    const checkForLotChanges = useCallback(async (lotNumber: string, revisionId: number, status: string, processingStatus: string, noOfComments: number, noOfPackages: number, signal?: AbortSignal) => {
        var lotHasChanged = false;
        let url = `api/v1/lot/${lotNumber}/changes?revisionId=${revisionId}&status=${status}&processingStatus=${processingStatus}&numberOfComments=${noOfComments}&numberOfPackages=${noOfPackages}`;

        await fetchDataAuthenticated(url, "GET", null, signal)
            .then(async response => {
                if (response.ok) {
                    lotHasChanged = await response.json();
                    return;
                }
            });

        return lotHasChanged;
    }, [fetchDataAuthenticated]);

    const updateLotStatus = useCallback(async (status: string, comment: string, reasonCode?: string, message?: string) => {
        let url = `/api/v1/lot/${lotNumber}/status/${status}`;

        if (reasonCode) {
            url += "?reasoncode=" + reasonCode;
        }

        setLoadingLotDetails(true);
        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(comment))
            .then(async response => {
                if (response.ok) {
                    // Statuses from API controller: processing, readyToGrade, graded, undoGraded, lotAndProcessing
                    switch (status) {
                        case (LotStatus.Processing):
                        case (LotStatus.ReadyToGrade):
                        case (LotStatus.Graded): {
                            setAlert("success", "Success", t<string>(`LotStatusUpdateMessages.${status}`));
                            break;
                        }
                        default: {
                            setAlert("success", "Success",
                                message ??
                                t<string>('LotStatusUpdateMessages.Generic', {
                                    status: t<string>(`LotStatus.${status}`)
                                })
                            );
                            break;
                        }
                    }
                    getLotDetails();
                    updateCounters();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the lot status: " + errorMessage);
                    throw new Error("Failed to update the lot status. " + errorMessage);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoadingLotDetails(false);
            });
    }, [fetchDataAuthenticated, getLotDetails, lotNumber, setAlert, t, updateCounters]);

    return { lotDetails, getLotDetails, setLotDetails, getLotQualities, getLotGenealogy, checkForLotChanges, updateLotStatus, loadingLotDetails, loadingQualities, loadingGenealogy, errorCode };
}

export function useGetLotDetails() {
    const { fetchDataAuthenticated } = useHttpUtils();
    const [errorStatus, setErrorStatus] = useState<number>();

    const getLotDetails = useCallback((lotNumber: string, revisionId?: number,) => {
        let url = `api/v1/lot/${lotNumber}/detail`;
        if (revisionId) {
            url += `?revisionId=${revisionId}`;
        }

        return fetchDataAuthenticated(url)
            .then(response => {
                if (response.ok) {
                    return response.text();
                }

                setErrorStatus(response.status);
                throw new Error(`Failed to get lot detail for lot ${lotNumber}`);
            })
            .then(text => JSON.parse(text, luxonReviver))
            .then((data: Lot) => {
                return data;
            })
            .catch(error => {
                const errorResponse: ErrorResponse = { statusCode: errorStatus ?? 500 };

                if (errorStatus === 404) {
                    errorResponse.message = "Lot not found";
                } else if (errorStatus === 403) {
                    errorResponse.message = "Access denied";
                } else {
                    errorResponse.message = "An error occurred while fetching lot details";
                }

                return errorResponse;
            });

    }, [errorStatus, fetchDataAuthenticated]);

    return { getLotDetails };
}

export default useLotDetails;